import React from "react";
import { formatDate } from "%/date";
import { Button } from "@/Button";
import DateButton from "@/common/DateButton";
import MessageModal from "@/MessageModal";
import { Dialog } from "@/Dialog";
import ToggleSwitch from "@/common/ToggleSwitch";
import { ValidateSelectList } from "@/SelectList";
import { ValidateSelectListMulti } from "@/SelectListMulti";
import { ValidateTextarea } from "@/Textarea";
import { Controller } from "react-hook-form";
import { CalendarNew as CalendarComponent } from "@/CalendarNew";
import { useRouter } from "@tanstack/react-router";
import useNotifySendSchedule, {
  type GalleryImage,
} from "^/business/notify/useNotifySendSchedule";
import { ValidateInput } from "@/Input";
import { groupRecipientOptions } from "./groupedRecipientOptions";

// NOTE(사진 첨부 미구현)
// 일정 공지는 useNotifySendSchedule 에 앨범 조회(albumData)가 없어
// 사진 목록을 서버에서 가져오지 못한다.
// 이전에는 여기에 외부 스톡 이미지(unsplash) 2장을 더미로 두고 있었으나,
// 다른 사업장의 사진이 학부모에게 발송될 수 있어 제거했다.
// 앨범 연동은 useNotifySendNotify 의 fetchAlbumList 패턴을 따르면 된다.
const galleryData: GalleryImage[] = [];

const SendSchedule = () => {
  const router = useRouter();
  const {
    // 폼 관련
    formMethods: {
      handleSubmit,
      control,
      register,
      // watch,
      formState: { isValid, errors },
    },

    // 선택 목록 데이터
    selectListData,

    // 날짜 관련
    date,
    sendDate,
    setSendDate,
    scheduleDates,
    setScheduleDates,
    selectedImgDate,
    setSelectedImgDate,

    // 모달 관련
    calendarModal: { isOpenModal, clickModal, closeModal },
    imageModal: { isOpenModal: isOpenImageModal, closeModal: closeImageModal },
    imgDateModal: {
      isOpenModal: isOpenImgDateModal,
      closeModal: closeImgDateModal,
    },

    // 이미지 관련
    // selectedImages,
    modalSelectedImages,
    toggleImageSelection,
    // removeSelectedImage,
    handleImgModal,

    // 토글 관련
    // isToggle,
    // setIsToggle,
    isTimeToggle,
    // setIsTimeToggle, // handleTimeToggleChange에서 처리
    handleTimeToggleChange,

    // 다이얼로그 관련
    showSuccessDialog,
    setShowSuccessDialog,

    // 제출 관련
    bindSubmit,
    isSubmitting,
  } = useNotifySendSchedule();

  // 같은 보호자의 반려견은 옵션 하나로 묶음 — "초코, 보리 (홍길동)"
  const recipientOptions = React.useMemo(
    () => groupRecipientOptions(selectListData?.recipients),
    [selectListData?.recipients]
  );

  // 디버그 로그 추가
  React.useEffect(() => {
    console.log("SendSchedule - isValid:", isValid, "errors:", errors);
  }, [isValid, errors]);

  return (
    <>
      {/* 달력 영역이 form 바깥에 이어지므로 하단 패딩(pb-12)을 두지 않는다.
          두면 내용 입력과 달력 사이에 48px 빈 공간이 생긴다 (편집 화면 SendSavedSchedule 과 동일 구성). */}
      <form className="flex-col flex-auto gap-1 px-5 bg-[#F5F5F5] grid grid-cols-1 animate-fadeIn">
        <div className="mb-5 animate-slideInFromTop">
          <Controller
            name="recipient"
            control={control}
            rules={{ required: "필수 선택입니다." }}
            render={({ field }) => (
              <ValidateSelectListMulti
                onChange={(selectedValues) => {
                  field.onChange(selectedValues);
                }}
                label="수신"
                placeholder="반려견 이름 선택 (다중선택 가능)"
                options={recipientOptions}
                value={
                  Array.isArray(field.value)
                    ? field.value
                    : field.value
                      ? [field.value]
                      : []
                }
                errorMessage={errors?.recipient?.message}
              />
            )}
          />
          <div className="my-4">
            <ValidateInput
              className="w-full"
              label="제목"
              placeholder="제목을 입력하세요."
              errorMessage={errors?.notiTitle?.message}
              {...register("notiTitle", {
                required: { value: true, message: "제목을 입력하세요" },
              })}
            />
          </div>
          <div
            className="my-5 animate-slideInFromTop"
            style={{ animationDelay: "0.1s" }}
          >
            <div className="flex justify-between items-center mb-1">
              <p className="mb-2 text-sm font-medium">발송시간</p>
              <ToggleSwitch
                checked={isTimeToggle}
                onChange={handleTimeToggleChange}
                // disabled={isUpdating}
              />
            </div>
            {isTimeToggle && (
              <div className="flex gap-1 items-center mb-1">
                <DateButton
                  value={sendDate ? `${formatDate(sendDate)}` : date}
                  onClick={() => {
                    // 모달을 열 때 기본값으로 오늘 날짜 설정
                    if (!sendDate) {
                      setSendDate(new Date());
                    }
                    clickModal();
                  }}
                />
                <Controller
                  name="hour"
                  control={control}
                  rules={{ required: "시간을 선택하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) => {
                        if (
                          selectedOption &&
                          !Array.isArray(selectedOption) &&
                          "value" in selectedOption
                        ) {
                          field.onChange(selectedOption.value);
                        } else {
                          field.onChange("");
                        }
                      }}
                      isDefaultValue={true}
                      placeholder="시"
                      isSearchable={false}
                      options={Array.from({ length: 24 }, (_, i) => ({
                        value: (i + 1).toString().padStart(2, "0"),
                        label: (i + 1).toString().padStart(2, "0"),
                      }))}
                      boxHeight={20}
                      className="h-10 text-lg text-center"
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: field.value,
                            }
                          : null
                      }
                    />
                  )}
                />
                <span className="text-[12px]">시</span>
                <Controller
                  name="minute"
                  control={control}
                  rules={{ required: "분을 선택하세요" }}
                  render={({ field }) => (
                    <ValidateSelectList
                      onChange={(selectedOption) => {
                        if (
                          selectedOption &&
                          !Array.isArray(selectedOption) &&
                          "value" in selectedOption
                        ) {
                          field.onChange(selectedOption.value);
                        } else {
                          field.onChange("");
                        }
                      }}
                      isDefaultValue={true}
                      placeholder="분"
                      isSearchable={false}
                      options={Array.from({ length: 6 }, (_, i) => ({
                        value: (i * 10).toString().padStart(2, "0"),
                        label: (i * 10).toString().padStart(2, "0"),
                      }))}
                      boxHeight={20}
                      className="h-10 text-lg text-center"
                      value={
                        field.value
                          ? {
                              value: field.value,
                              label: field.value,
                            }
                          : null
                      }
                    />
                  )}
                />
                <span className="text-[12px]">분</span>
              </div>
            )}
          </div>
          <div
            className="my-5 animate-slideInFromTop"
            style={{ animationDelay: "0.2s" }}
          >
            <Controller
              name="content"
              control={control}
              rules={{ required: "필수 선택입니다." }}
              render={({ field }) => (
                <ValidateTextarea
                  {...field}
                  className="w-full h-100"
                  label="내용"
                  rows={10}
                />
              )}
            />
          </div>
        </div>
      </form>
      <div
        className="my-5 animate-slideInFromTop"
        style={{ animationDelay: "0.3s" }}
      >
        <CalendarComponent
          disabled={false}
          disablePast={true}
          multiSelect={true}
          selectedDays={scheduleDates}
          onChange={(days) => {
            if (Array.isArray(days)) {
              setScheduleDates(days);
            }
          }}
          schedules={{}}
        />
        {/* 선택된 날짜들 표시 */}
        {scheduleDates.length > 0 && (
          <div className="p-3 mt-3 bg-gray-50 rounded-lg">
            <div className="flex justify-between items-center mb-2">
              <p className="text-sm font-medium text-gray-700">
                선택된 스케줄 날짜:
              </p>
              <button
                type="button"
                onClick={() => {
                  setScheduleDates([]);
                }}
                className="text-xs text-red-500 hover:text-red-700"
              >
                전체 삭제
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {scheduleDates.map((date, index) => (
                <span
                  key={`schedule-${index}`}
                  className="px-2 py-1 text-xs text-white bg-green-500 rounded-full"
                >
                  {formatDate(date)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
      <div
        className="fixed left-1/2 -translate-x-1/2 w-full max-w-[480px] bottom-0 md:bottom-6 md:rounded-b-3xl z-40 px-4 pt-3 pb-[calc(0.75rem+var(--nav-bottom,0px)+env(safe-area-inset-bottom,0px))] bg-white border-t border-gray-200 shadow-lg flex gap-2"
      >
        <Button
          className="w-full bg-transparent"
          style={{ pointerEvents: "auto" }}
          onClick={handleSubmit((data) => {
            console.log("SendSchedule - 저장하기 버튼 클릭, data:", data);
            bindSubmit(data, true);
          })}
          disabled={!isValid || isSubmitting}
        >
          <div className="py-3 mx-auto w-full font-semibold text-center text-yellow-500 bg-white rounded-full border border-yellow text-[16px] text-yellow">
            저장하기
          </div>
        </Button>
        <Button
          className="w-full bg-transparent"
          style={{ pointerEvents: "auto" }}
          onClick={handleSubmit((data) => {
            console.log("SendSchedule - 즉시 발송 버튼 클릭, data:", data);
            bindSubmit(data, false);
          })}
          disabled={!isValid || isSubmitting}
        >
          <div className="py-3 mx-auto w-full font-semibold text-center text-white bg-orange-400 rounded-full text-[16px]">
            {isTimeToggle ? "예약 발송" : "즉시 발송"}
          </div>
        </Button>
      </div>
      {/* 모달 영역 */}
      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenModal}
        onRequestClose={closeModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          disablePast={true}
          multiSelect={false}
          selectedDay={sendDate}
          dayOnClick={(day) => {
            setSendDate(day);
          }}
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={() => {
              setSendDate(null); // 발송 날짜 초기화
              closeModal();
            }}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={() => {
              closeModal();
              console.log(
                "발송 날짜 선택 완료:",
                sendDate ? formatDate(sendDate) : "선택 안됨"
              );
            }}
          >
            확인
          </button>
        </div>
      </MessageModal>
      <MessageModal
        isOpen={isOpenImgDateModal}
        onRequestClose={closeImgDateModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <CalendarComponent
          disabled={false}
          dayOnClick={(day) => {
            setSelectedImgDate(day);
          }}
          selectedDay={selectedImgDate}
          schedules={{}}
        />
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeImgDateModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={closeImgDateModal}
          >
            확인
          </button>
        </div>
      </MessageModal>
      {/* 달력 모달 */}
      <MessageModal
        isOpen={isOpenImageModal}
        onRequestClose={closeImageModal}
        className="pt-0 pb-6 mx-auto w-full max-w-sm bg-white rounded-2xl border-2 border-gray-200 shadow-lg outline-none"
        overlayClassName="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
        shouldCloseOnOverlayClick={false}
      >
        <div className="flex flex-col gap-2 overflow-y-auto h-[500px]">
          {galleryData.length === 0 ? (
            <div className="flex flex-col flex-1 gap-1 justify-center items-center px-6 text-center">
              <p className="text-gray-600">불러올 사진이 없습니다.</p>
              <p className="text-sm text-gray-400">
                사진첩에서 먼저 사진을 업로드해주세요.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-1 sm:grid-cols-4">
              {galleryData.map((data) => (
                <div
                  key={data.ImgID}
                  className={`relative cursor-pointer aspect-square border-2 rounded-lg ${
                    modalSelectedImages?.find(
                      (item) => item.ImgID === data.ImgID
                    )
                      ? " border-orange-500"
                      : "border-white bg-white"
                  }`}
                  onClick={() => toggleImageSelection(data)}
                >
                  <img
                    src={data.ImgURL}
                    alt={`Gallery image ${data.ImgID}`}
                    className="object-cover w-full h-full rounded-lg"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-3 mt-2">
          <button
            className="flex-1 py-2 font-semibold text-gray-700 bg-gray-200 rounded-full border border-gray-300"
            onClick={closeImageModal}
          >
            취소
          </button>
          <button
            className="flex-1 py-2 font-semibold text-white bg-orange-400 rounded-full"
            onClick={handleImgModal}
          >
            선택
          </button>
        </div>
      </MessageModal>

      {/* 성공 다이얼로그 */}
      <Dialog
        visible={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
      >
        <div className="text-center">
          <h3 className="mb-2 text-lg font-semibold">일정 공지 발송 완료</h3>
          <p className="mb-4 text-gray-600">
            일정 공지가 성공적으로 발송되었습니다.
          </p>
          <button
            onClick={() => {
              setShowSuccessDialog(false);
              router.navigate({ to: "/business/layout/notify" });
            }}
            className="px-4 py-2 text-white bg-orange-400 rounded-lg hover:bg-orange-500"
          >
            확인
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default SendSchedule;
